import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  AddOutlined,
  DeleteOutline,
  EditOutlined,
  PauseCircleOutline,
  PlayArrowOutlined,
  AutoFixHighOutlined,
  CodeOutlined,
  PreviewOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { GlobalContext } from "../../context/GlobalContext";
import { fillCampaignPreview } from "./campaignTemplates";

const emptyStep = () => ({
  day_offset: 0,
  subject: "",
  html_body: "",
});

/**
 * Mailchimp-style drip automations (signup journeys).
 */
export default function MarketingJourneys({ showSnack }) {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  /** Per-step body editor mode: 0 = HTML, 1 = Preview */
  const [stepBodyTab, setStepBodyTab] = React.useState({});
  const [form, setForm] = React.useState({
    id: null,
    name: "",
    description: "",
    trigger_type: "signup",
    steps: [emptyStep()],
  });

  const load = React.useCallback(async () => {
    setLoading(true);
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys",
      post: false,
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) setRows(res.data.data || []);
    setLoading(false);
  }, [hitAxios]);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setStepBodyTab({});
    setForm({
      id: null,
      name: "",
      description: "",
      trigger_type: "signup",
      steps: [
        { ...emptyStep(), day_offset: 0, subject: "Welcome — get your first win" },
        { ...emptyStep(), day_offset: 1, subject: "Day 2 tip" },
        { ...emptyStep(), day_offset: 2, subject: "Day 3 tip" },
      ],
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setStepBodyTab({});
    setForm({
      id: row.id,
      name: row.name || "",
      description: row.description || "",
      trigger_type: row.trigger_type || "signup",
      steps:
        row.steps?.length > 0
          ? row.steps.map((s) => ({
              day_offset: s.day_offset,
              subject: s.subject,
              html_body: s.html_body,
            }))
          : [emptyStep()],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return showSnack?.("Name is required", "warning");
    if (!form.steps.length) return showSnack?.("Add at least one step", "warning");
    setSaving(true);
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys/save",
      post: true,
      admin: true,
      obj: form,
      showSnackbar: false,
    });
    setSaving(false);
    if (res?.data?.success) {
      showSnack?.(res.data.msg || "Journey saved");
      setOpen(false);
      load();
    } else {
      showSnack?.(res?.data?.msg || "Save failed", "error");
    }
  };

  const setStatus = async (id, status) => {
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys/status",
      post: true,
      admin: true,
      obj: { id, status },
      showSnackbar: false,
    });
    if (res?.data?.success) {
      showSnack?.(status === "active" ? "Automation live" : `Status: ${status}`);
      load();
    } else {
      showSnack?.(res?.data?.msg || "Update failed", "error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this automation and its enrollment history?")) return;
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys/delete",
      post: true,
      admin: true,
      obj: { id },
      showSnackbar: false,
    });
    if (res?.data?.success) {
      showSnack?.("Deleted");
      load();
    } else {
      showSnack?.(res?.data?.msg || "Delete failed", "error");
    }
  };

  const seedWelcome = async () => {
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys/seed-welcome",
      post: true,
      admin: true,
      obj: {},
      showSnackbar: false,
    });
    if (res?.data?.success) {
      showSnack?.(
        "Welcome 30-day journey saved as draft — activate when ready",
      );
      load();
    } else {
      showSnack?.(res?.data?.msg || "Seed failed", "error");
    }
  };

  const seedMakeMoney = async () => {
    const res = await hitAxios({
      path: "/api/admin/marketing/journeys/seed-make-money",
      post: true,
      admin: true,
      obj: {},
      showSnackbar: false,
    });
    if (res?.data?.success) {
      showSnack?.(
        "Make-money 14-day journey saved as draft — activate when ready",
      );
      load();
    } else {
      showSnack?.(res?.data?.msg || "Seed failed", "error");
    }
  };

  const updateStep = (idx, patch) => {
    setForm((p) => ({
      ...p,
      steps: p.steps.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  };

  const bodyTabFor = (idx) => stepBodyTab[idx] ?? 0;

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Automations enroll users on <strong>signup</strong> when status is Active. Day N =
        enrolled_at + N×24h. Seeds use spaced cadence (not one email every day). Opt-outs are
        skipped. If both journeys are active, new users enroll in both.
      </Alert>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
        <Button
          variant="contained"
          startIcon={<AddOutlined />}
          onClick={openCreate}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          New automation
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighOutlined />}
          onClick={seedWelcome}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Seed 30-day welcome
        </Button>
        <Button
          variant="outlined"
          startIcon={<AutoFixHighOutlined />}
          onClick={seedMakeMoney}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          Seed 14-day make money
        </Button>
      </Stack>

      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          overflow: "hidden",
          bgcolor: "background.paper",
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
              {["Automation", "Trigger", "Steps", "Status", "Active / Done", ""].map((h) => (
                <TableCell key={h || "a"} sx={{ fontWeight: 700 }}>
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                    No automations yet. Seed welcome / make-money series or create one.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Typography fontWeight={700}>{row.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.description || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.trigger_type} />
                  </TableCell>
                  <TableCell>
                    {(row.steps || [])
                      .map((s) => `D${s.day_offset}`)
                      .join(" → ") || "—"}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={
                        row.status === "active"
                          ? "success"
                          : row.status === "paused"
                            ? "warning"
                            : "default"
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {row.active_enrollments || 0} / {row.completed_enrollments || 0}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <IconButton size="small" onClick={() => openEdit(row)}>
                        <EditOutlined fontSize="small" />
                      </IconButton>
                      {row.status === "active" ? (
                        <IconButton
                          size="small"
                          onClick={() => setStatus(row.id, "paused")}
                          title="Pause"
                        >
                          <PauseCircleOutline fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          onClick={() => setStatus(row.id, "active")}
                          title="Activate"
                          color="success"
                        >
                          <PlayArrowOutlined fontSize="small" />
                        </IconButton>
                      )}
                      <IconButton size="small" color="error" onClick={() => remove(row.id)}>
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.id ? "Edit automation" : "New automation"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="Description"
              size="small"
              fullWidth
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            />
            <Typography variant="subtitle2" fontWeight={800}>
              Steps (day offset from signup)
            </Typography>
            {form.steps.map((step, idx) => {
              const tab = bodyTabFor(idx);
              const previewHtml = fillCampaignPreview(step.html_body || "", {
                campaign_title: form.name || "Automation preview",
                user_name: "Alex",
              });
              return (
                <Box
                  key={idx}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    bgcolor: alpha(theme.palette.text.primary, 0.02),
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
                    <TextField
                      label="Day"
                      type="number"
                      size="small"
                      value={step.day_offset}
                      onChange={(e) =>
                        updateStep(idx, { day_offset: Number(e.target.value) })
                      }
                      inputProps={{ min: 0, max: 365 }}
                      sx={{ width: 100 }}
                      helperText="0 = immediate"
                    />
                    <TextField
                      label="Subject"
                      size="small"
                      fullWidth
                      value={step.subject}
                      onChange={(e) => updateStep(idx, { subject: e.target.value })}
                    />
                    <IconButton
                      color="error"
                      disabled={form.steps.length <= 1}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          steps: p.steps.filter((_, i) => i !== idx),
                        }))
                      }
                    >
                      <DeleteOutline />
                    </IconButton>
                  </Stack>

                  <Tabs
                    value={tab}
                    onChange={(_, v) =>
                      setStepBodyTab((p) => ({ ...p, [idx]: v }))
                    }
                    sx={{
                      minHeight: 36,
                      mb: 1.5,
                      borderBottom: 1,
                      borderColor: "divider",
                      "& .MuiTab-root": {
                        minHeight: 36,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 13,
                      },
                    }}
                  >
                    <Tab
                      icon={<CodeOutlined sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="HTML"
                    />
                    <Tab
                      icon={<PreviewOutlined sx={{ fontSize: 16 }} />}
                      iconPosition="start"
                      label="Preview"
                    />
                  </Tabs>

                  {tab === 0 ? (
                    <TextField
                      label="HTML body"
                      size="small"
                      fullWidth
                      multiline
                      minRows={8}
                      value={step.html_body}
                      onChange={(e) => updateStep(idx, { html_body: e.target.value })}
                      helperText="Merge tags: {{user_name}}, {{login_url}}, {{unsubscribe_url}}, {{date}}. Switch to Preview for a plain rendered view."
                      InputProps={{
                        sx: {
                          fontFamily:
                            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                          fontSize: 12.5,
                        },
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        bgcolor: "#e2e8f0",
                        p: { xs: 1.5, sm: 2 },
                        borderRadius: 1.5,
                        minHeight: 280,
                        maxHeight: 420,
                        overflow: "auto",
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        mb={1}
                        textAlign="center"
                      >
                        Preview · Subject:{" "}
                        <strong>{step.subject || "(no subject)"}</strong>
                      </Typography>
                      <Box
                        sx={{
                          maxWidth: 560,
                          mx: "auto",
                          bgcolor: "#fff",
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid #cbd5e1",
                          "& img": { maxWidth: "100%" },
                        }}
                        dangerouslySetInnerHTML={{
                          __html:
                            previewHtml ||
                            "<p style='padding:24px;color:#64748b;text-align:center;font-family:Arial,sans-serif'>No HTML yet — switch to HTML to edit.</p>",
                        }}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
            <Button
              startIcon={<AddOutlined />}
              onClick={() =>
                setForm((p) => ({
                  ...p,
                  steps: [
                    ...p.steps,
                    {
                      ...emptyStep(),
                      day_offset:
                        (p.steps[p.steps.length - 1]?.day_offset ?? 0) + 1,
                    },
                  ],
                }))
              }
              sx={{ alignSelf: "flex-start", textTransform: "none" }}
            >
              Add day
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <LoadingButton loading={saving} variant="contained" onClick={save}>
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
